import { useState } from "react";
import { X, Ban, ChevronDown, CheckCircle, XCircle } from "lucide-react";
import useClickOutside from "../../../../CustomHooks/useClickOutside";
import UsePermissionsClaim from "../../../../CustomHooks/UsePermissionsClaim";

const buttonBaseClasses =
  "flex items-center gap-1 px-2 py-[4px] border border-gray-300 rounded-md max-lg:text-sm";

const UserBulkActions = ({
  bulkActionType,
  setBulkActionType,
  selectedCount,
  setShowBulkActionDialog,
  setSelectedUsers,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { dropdownRef } = useClickOutside(() => setIsOpen(false));

  const { checkPermission } = UsePermissionsClaim();

  // Show only bulk actions the user is authorized to perform
  const visibleBulkActions = BULK_ACTIONS.filter((action) =>
    checkPermission(action.requiredPermission)
  );

  const handleActionSelect = (type) => {
    setSelectedUsers([]);
    setBulkActionType(type);
  };

  // Cancel selection mode
  const onCancel = () => {
    setBulkActionType(null);
    setSelectedUsers([]);
  };

  return (
    <>
      {/* Action Confirmation */}
      {bulkActionType && (
        <div className="flex items-center gap-1 lg:gap-2">
          {selectedCount > 0 && (
            <span className="text-gray-700">{selectedCount} Selected</span>
          )}
          <button
            onClick={() => setShowBulkActionDialog(true)}
            disabled={selectedCount === 0}
            className={`${buttonBaseClasses} text-white backdrop:${
              selectedCount === 0 ? "cursor-not-allowed opacity-50 " : " "
            }${
              bulkActionType === "Disable"
                ? "bg-red-500 hover:bg-red-600"
                : bulkActionType === "Verify"
                ? "bg-teal-600 hover:bg-teal-500"
                : "bg-amber-600 hover:bg-amber-500"
            }`}
          >
            {bulkActionType === "Disable" && <Ban size={16} />}
            {bulkActionType === "Verify" && <CheckCircle size={16} />}
            {bulkActionType === "Unverify" && <XCircle size={16} />}
            {bulkActionType}
          </button>

          <button
            onClick={() => {
              setBulkActionType(null);
              onCancel();
            }}
            className={`${buttonBaseClasses} bg-gray-100 hover:bg-gray-200 text-gray-800`}
          >
            <X size={16} />
            Cancel
          </button>
        </div>
      )}
      {/* Bulk Action Button */}
      <div ref={dropdownRef} className="relative">
        <button
          onClick={() => setIsOpen((prev) => !prev)}
          className={`${buttonBaseClasses} bg-dBlue-100 hover:bg-gray-200 text-gray-800`}
        >
          <span>Bulk Actions</span>
          <ChevronDown size={18} />
        </button>
        {/* Bulk Action Dropdown */}
        {isOpen && (
          <div className="absolute top-[38px] left-0 w-44 bg-white border border-gray-300 rounded-md shadow-md z-9999 text-sm">
            {visibleBulkActions.map(({ type, label, Icon, iconClass }) => {
              return (
                <button
                  key={type}
                  onClick={() => handleActionSelect(type)}
                  className="flex items-center w-full p-2 hover:bg-gray-100 border-b border-gray-200 last:border-b-0"
                >
                  <Icon size={16} className={`mr-2 ${iconClass}`} />
                  {label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default UserBulkActions;

const BULK_ACTIONS = [
  {
    type: "Verify",
    label: "Verify user",
    Icon: CheckCircle,
    iconClass: "text-teal-600",
    requiredPermission: "user_phone_verify",
  },
  {
    type: "Unverify",
    label: "Unverify user",
    Icon: XCircle,
    iconClass: "text-amber-600",
    requiredPermission: "user_phone_verify",
  },
  {
    type: "Disable",
    label: "Disable user",
    Icon: Ban,
    iconClass: "text-red-500",
    requiredPermission: "disable_user",
  },
];
